import { supabase } from '@/integrations/supabase/client';

export interface AccrualHistoryEntry {
  id: string;
  company_id: string;
  profile_id: string | null;
  policy_id: string;
  event_type: 'accrual' | 'deduction' | 'adjustment' | 'rollover' | 'expiration' | 'policy_change';
  event_subtype?: string;
  amount_days?: number;
  amount_hours?: number;
  balance_before_days?: number;
  balance_before_hours?: number;
  balance_after_days?: number;
  balance_after_hours?: number;
  reference_id?: string;
  reference_type?: string;
  description: string;
  metadata?: Record<string, any>;
  performed_by?: string;
  performed_at: string;
  created_at: string;
  deleted_employee_name?: string | null;
  deleted_performer_name?: string | null;
}

/** Shape returned by PostgREST join query */
export interface AccrualHistoryWithJoins extends AccrualHistoryEntry {
  profiles: { first_name: string; last_name: string; email: string } | null;
  leave_policies: { id: string; name: string; leave_type: string } | null;
  performer: { first_name: string; last_name: string; email: string } | null;
}

export interface CreateAccrualHistoryInput {
  profile_id: string;
  policy_id: string;
  event_type: AccrualHistoryEntry['event_type'];
  event_subtype?: string;
  amount_days?: number;
  amount_hours?: number;
  balance_before_days?: number;
  balance_before_hours?: number;
  balance_after_days?: number;
  balance_after_hours?: number;
  reference_id?: string;
  reference_type?: string;
  description: string;
  metadata?: Record<string, any>;
  performed_by?: string;
}

export interface AccrualHistoryFilters {
  profile_id?: string;
  policy_id?: string;
  event_type?: string;
  event_subtype?: string;
  reference_type?: string;
  start_date?: string;
  end_date?: string;
  search_term?: string;
  limit?: number;
  offset?: number;
}

const handleAccrualHistoryError = (error: any, operation: string) => {
  console.error(`Accrual History Service - ${operation}:`, error);
  throw error;
};

/**
 * Apply common filters to an accrual_history query builder.
 */
const applyFilters = (query: any, filters: AccrualHistoryFilters) => {
  if (filters.profile_id) {
    query = query.eq('profile_id', filters.profile_id);
  }
  if (filters.policy_id) {
    query = query.eq('policy_id', filters.policy_id);
  }
  if (filters.event_type) {
    query = query.eq('event_type', filters.event_type);
  }
  if (filters.event_subtype) {
    query = query.eq('event_subtype', filters.event_subtype);
  }
  if (filters.reference_type) {
    query = query.eq('reference_type', filters.reference_type);
  }
  if (filters.start_date) {
    query = query.gte('performed_at', filters.start_date);
  }
  if (filters.end_date) {
    query = query.lte('performed_at', filters.end_date);
  }
  if (filters.search_term) {
    query = query.ilike('description', `%${filters.search_term}%`);
  }
  return query;
};

export const accrualHistoryService = {
  /**
   * Record a new accrual history entry
   */
  async recordEntry(input: CreateAccrualHistoryInput): Promise<AccrualHistoryEntry> {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!profile?.company_id) {
        throw new Error('User profile not found or no company associated');
      }

      const { data, error } = await supabase
        .from('accrual_history')
        .insert({
          company_id: profile.company_id,
          ...input,
        })
        .select()
        .single();

      if (error) {
        handleAccrualHistoryError(error, 'recordEntry');
      }

      return data as AccrualHistoryEntry;
    } catch (error) {
      handleAccrualHistoryError(error, 'recordEntry');
      throw error;
    }
  },

  /**
   * Get accrual history with PostgREST joins (profiles, leave_policies, performer).
   * Single query replaces the previous getHistory + getDetailedHistory pattern.
   */
  async getHistory(filters: AccrualHistoryFilters = {}): Promise<{
    data: AccrualHistoryWithJoins[];
    total: number;
  }> {
    try {
      let query = supabase
        .from('accrual_history')
        .select(`
          *,
          profiles!accrual_history_profile_id_fkey(first_name, last_name, email),
          leave_policies!accrual_history_policy_id_fkey(id, name, leave_type),
          performer:profiles!accrual_history_performed_by_fkey(first_name, last_name, email)
        `, { count: 'exact' });

      query = applyFilters(query, filters);

      // Apply pagination
      const limit = filters.limit || 50;
      const offset = filters.offset || 0;
      query = query.range(offset, offset + limit - 1);

      // Order by most recent first
      query = query.order('performed_at', { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        handleAccrualHistoryError(error, 'getHistory');
      }

      return { data: (data || []) as AccrualHistoryWithJoins[], total: count || 0 };
    } catch (error) {
      handleAccrualHistoryError(error, 'getHistory');
      throw error;
    }
  },

  /**
   * Get summary statistics for accrual history
   */
  async getHistorySummary(filters: AccrualHistoryFilters = {}): Promise<{
    totalEntries: number;
    eventTypeCounts: Record<string, number>;
    totalDaysAccrued: number;
    totalDaysDeducted: number;
  }> {
    try {
      // Use a lightweight query without joins for summary
      let query = supabase
        .from('accrual_history')
        .select('event_type, amount_days', { count: 'exact' });

      query = applyFilters(query, filters);

      const { data, error, count } = await query;

      if (error) {
        handleAccrualHistoryError(error, 'getHistorySummary');
      }

      const entries = data || [];
      const summary = {
        totalEntries: count || 0,
        eventTypeCounts: {} as Record<string, number>,
        totalDaysAccrued: 0,
        totalDaysDeducted: 0,
      };

      entries.forEach((entry: any) => {
        summary.eventTypeCounts[entry.event_type] =
          (summary.eventTypeCounts[entry.event_type] || 0) + 1;

        const days = entry.amount_days || 0;
        if (entry.event_type === 'accrual' || entry.event_type === 'adjustment') {
          if (days > 0) {
            summary.totalDaysAccrued += days;
          } else {
            summary.totalDaysDeducted += Math.abs(days);
          }
        } else if (entry.event_type === 'deduction') {
          summary.totalDaysDeducted += Math.abs(days);
        }
      });

      return summary;
    } catch (error) {
      handleAccrualHistoryError(error, 'getHistorySummary');
      throw error;
    }
  },
};
